import {
  parseAlignments,
  parseBreaklines,
  parseParcels,
  parsePipeNetworks,
  parseSurfaces,
  type LandXMLFeature,
} from '@/core/landxml';

/** Per-layer quantity totals, shown in the LandXML block of `LayerProperties`. */
export interface LandxmlSummary {
  surfaceCount: number;
  surfaceArea: number;
  alignmentCount: number;
  alignmentLength: number;
  parcelCount: number;
  parcelArea: number;
  breaklineCount: number;
  breaklineLength: number;
  pipeCount: number;
  pipeLength: number;
  /** `"300 mm"` → count and total length of the pipes with that diameter. */
  pipesByDiameter: Map<string, { count: number; length: number }>;
  structureCount: number;
}

const empty = (): LandxmlSummary => ({
  surfaceCount: 0,
  surfaceArea: 0,
  alignmentCount: 0,
  alignmentLength: 0,
  parcelCount: 0,
  parcelArea: 0,
  breaklineCount: 0,
  breaklineLength: 0,
  pipeCount: 0,
  pipeLength: 0,
  pipesByDiameter: new Map(),
  structureCount: 0,
});

/**
 * Total up a parsed LandXML document.
 *
 * Deliberately built on the same `parse*` readers the viewer draws from, so
 * the reported quantities can never disagree with what is on screen. That
 * means the document is walked in full — the caller is expected to cache the
 * result (`LayerProperties` keys a react-query entry by layer id).
 *
 * Throws `LandxmlNonMetreUnitsError` for a non-metre file, like every other
 * reader: mis-scaled quantities are worse than none.
 */
export function summarizeLandxml(doc: Document): LandxmlSummary {
  const surfaces = parseSurfaces(doc);
  const alignments = parseAlignments(doc);
  const parcels = parseParcels(doc);
  const breaklines = parseBreaklines(doc);
  const { meshes: pipeMeshes, lines: pipeLines } = parsePipeNetworks(doc);
  return summarize(surfaces, alignments, parcels, breaklines, pipeMeshes, pipeLines);
}

function summarize(
  surfaces: LandXMLFeature[],
  alignments: LandXMLFeature[],
  parcels: LandXMLFeature[],
  breaklines: LandXMLFeature[],
  pipeMeshes: LandXMLFeature[],
  pipeLines: LandXMLFeature[],
): LandxmlSummary {
  const out = empty();
  for (const s of surfaces) {
    out.surfaceCount++;
    out.surfaceArea += s.metadata.surfaceArea ?? 0;
  }
  for (const a of alignments) {
    out.alignmentCount++;
    out.alignmentLength += a.metadata.length ?? 0;
  }
  for (const p of parcels) {
    out.parcelCount++;
    out.parcelArea += p.metadata.parcelArea ?? 0;
  }
  for (const b of breaklines) {
    out.breaklineCount++;
    out.breaklineLength += b.metadata.length ?? 0;
  }
  // Pipes: count + length come from the centerline list (one per pipe);
  // structure mesh count is the structure total. The mesh list also has
  // PIPE-category entries (the cylinders) — skip those for length so we
  // don't double-count.
  for (const m of pipeMeshes) {
    if (m.category === 'STRUCTURE') out.structureCount++;
  }
  for (const l of pipeLines) {
    if (l.category !== 'PIPE') continue;
    out.pipeCount++;
    const len = l.metadata.length ?? 0;
    out.pipeLength += len;
    const d = l.metadata.diameter;
    if (d !== undefined) {
      const key = `${(d * 1000).toFixed(0)} mm`;
      const entry = out.pipesByDiameter.get(key) ?? { count: 0, length: 0 };
      entry.count++;
      entry.length += len;
      out.pipesByDiameter.set(key, entry);
    }
  }
  return out;
}

/** Diameter buckets, smallest first — the order the table renders in. */
export function sortedDiameters(summary: LandxmlSummary): [string, { count: number; length: number }][] {
  return Array.from(summary.pipesByDiameter.entries()).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
}
